import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function Maintenance() {
  const maintenancePricing = [
    {
      type: 'Small Cars',
      price: '$80',
      description: 'Regular maintenance for compact vehicles',
      features: ['Interior touch-up', 'Exterior wash & dry', 'Quick vacuum', 'Dashboard cleaning']
    },
    {
      type: 'Medium Cars',
      price: '$90',
      description: 'Maintenance service for mid-size vehicles',
      features: ['Complete interior refresh', 'Exterior wash & wax', 'Vacuum & wipe down', 'Window cleaning']
    },
    {
      type: 'Trucks',
      price: '$100',
      description: 'Maintenance for trucks and large vehicles',
      features: ['Full interior cleaning', 'Exterior wash & protection', 'Deep vacuum', 'All surface cleaning']
    }
  ];

  const howItWorksSteps = [
    {
      step: '1',
      title: 'Book Your First Full Detail',
      description: 'Start with a complete full detail service to establish the baseline for your vehicle.',
      icon: 'ri-car-washing-line'
    },
    {
      step: '2',
      title: 'Schedule Regular Maintenance',
      description: 'We return every 2-3 weeks to maintain your car\'s pristine condition.',
      icon: 'ri-calendar-check-line'
    },
    {
      step: '3',
      title: 'Enjoy Consistent Results',
      description: 'Fresh interior & exterior touch-ups keep your vehicle looking its best.',
      icon: 'ri-star-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-red-600 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Maintenance Plans</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Keep your vehicle looking pristine with our regular maintenance program
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">How It Works</h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-black mb-8">
                After your first full detail, we come back every 2–3 weeks to keep your car looking its best. 
                This includes a fresh interior & exterior touch-up to maintain that just-detailed appearance.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-red-600 text-white rounded-full mx-auto mb-6">
                  <i className={`${step.icon} text-2xl`}></i>
                </div>
                <div className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{step.title}</h3>
                <p className="text-black">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Policy */}
      <section className="py-16 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-100 border border-red-600 rounded-xl p-8 max-w-4xl mx-auto">
            <div className="flex items-start">
              <div className="w-8 h-8 flex items-center justify-center mr-4 mt-1">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-black mb-4">⚠️ Important Policy</h3>
                <p className="text-lg text-black">
                  If your maintenance appointment goes past 1.5 months, you must book a full detail again. 
                  This ensures we can maintain the quality standards you expect from our service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Maintenance Plan Pricing
            </h2>
            <p className="text-lg text-black">
              Affordable regular maintenance to keep your vehicle in top condition
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {maintenancePricing.map((plan, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow relative">
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-black mb-2">{plan.type}</h3>
                  <p className="text-black mb-4">{plan.description}</p>
                  <div className="text-4xl font-bold text-red-600 mb-4">{plan.price}</div>
                  <p className="text-sm text-black">Every 2-3 weeks</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-check-line text-red-600"></i>
                      </div>
                      <span className="text-black">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Benefits of Regular Maintenance
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full mr-4 mt-1">
                    <i className="ri-money-dollar-circle-line text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black mb-2">Cost Effective</h3>
                    <p className="text-black">Save money compared to frequent full details while maintaining quality.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full mr-4 mt-1">
                    <i className="ri-time-line text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black mb-2">Time Saving</h3>
                    <p className="text-black">Quick maintenance visits keep your car clean without long wait times.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full mr-4 mt-1">
                    <i className="ri-shield-check-line text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black mb-2">Vehicle Protection</h3>
                    <p className="text-black">Regular care protects your investment and maintains resale value.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full mr-4 mt-1">
                    <i className="ri-calendar-check-line text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black mb-2">Convenient Scheduling</h3>
                    <p className="text-black">Set it and forget it - we'll remind you when it's time for service.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:pl-8">
              <img 
                src="https://static.readdy.ai/image/e88ada64db3193888b9a84ae586990c8/553fcb60020b84f60635e4a17aae8749.jpeg"
                alt="Maintenance Benefits"
                className="rounded-xl shadow-lg w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Join Our Maintenance Plan?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Start with a full detail, then let us keep your vehicle looking perfect with regular maintenance visits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-black hover:text-white">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-message-3-line"></i>
                </div>
                Text to Join Maintenance Plan
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                View Full Detail Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
